import { ClassNames } from "@emotion/react";
import { faCalendar } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { darken } from "@material-ui/core";
import { DatePicker } from "@material-ui/pickers";
import { format, parseISO } from "date-fns";
import { forwardRef } from "react";

import { assertNever } from "@rewards-web/shared/lib/assert-never";
import {
  ControlledFormFieldProps,
  StylableProps,
} from "@rewards-web/shared/types";

import { useFormControlContext } from "../form/form-control";
import { DateFieldProvider } from "./provider";

const DATEONLY_FORMAT = "yyyy-MM-dd";
const MONTHONLY_FORMAT = "yyyy-MM";

export function formatDateOnly(date: Date): string {
  return format(date, DATEONLY_FORMAT);
}

export function formatMonthOnly(date: Date): string {
  return format(date, MONTHONLY_FORMAT);
}

export interface DateFieldProps
  extends StylableProps,
    ControlledFormFieldProps {
  label: string;
  variant?: "date" | "month";
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  readOnly?: boolean;
  hideHelperText?: boolean;
  greyReadOnly?: boolean;
  dontDisplayDayOfWeek?: boolean;
  height?: number;
  /**
   * @default false
   *
   * By default, MUI component labels are placed within the input when the input is empty.
   * This overrides that behaviour to have it stay at the top.
   */
  keepLabelAtTop?: boolean;
}

export const DateField = forwardRef(
  (
    {
      className,
      label,
      error,
      name,
      value,
      onChange,
      onBlur,
      variant = "date",
      minDate,
      maxDate,
      disabled,
      readOnly: readOnlyProp = false,
      hideHelperText,
      greyReadOnly,
      dontDisplayDayOfWeek = false,
      height,
      keepLabelAtTop = false,
    }: DateFieldProps,
    ref: any
  ): JSX.Element => {
    const { submitting, readOnly: contextReadOnly } = useFormControlContext();
    const readOnly = contextReadOnly || readOnlyProp;

    return (
      <DateFieldProvider>
        <ClassNames>
          {({ css, theme }) => (
            <DatePicker
              innerRef={ref}
              name={name}
              className={className}
              InputLabelProps={keepLabelAtTop ? { shrink: true } : {}}
              InputProps={{
                className: css`
                  background-color: ${theme.palette.background.paper};
                  border-radius: ${theme.spacing(1)};
                  ${height &&
                  css`
                    height: ${height}px;
                  `}

                  & .custom-notched-outline,
                  &.custom-disabled .custom-notched-outline {
                    border-color: ${theme.palette.divider};
                  }

                  &:hover:not(.Mui-error):not(.custom-focused)
                    .custom-notched-outline {
                    border-color: ${darken(theme.palette.divider, 0.15)};
                    border-width: 1px;
                  }

                  &.custom-focused .custom-notched-outline {
                    border-width: 1px;
                  }

                  ${readOnly &&
                  greyReadOnly &&
                  css`
                    background-color: #eaeaea;
                    color: #636666;
                    font-weight: bold;
                    input {
                      cursor: initial;
                    }
                  `}
                `,

                classes: {
                  root: "custom-root",
                  notchedOutline: "custom-notched-outline",
                  focused: "custom-focused",
                  disabled: "custom-disabled",
                },

                endAdornment: (
                  <FontAwesomeIcon
                    icon={faCalendar}
                    color={theme.palette.grey[800]}
                  />
                ),
              }}
              minDate={minDate}
              maxDate={maxDate}
              autoOk
              fullWidth
              variant="inline"
              inputVariant="outlined"
              label={label}
              readOnly={readOnly}
              format={(() => {
                switch (variant) {
                  case "date":
                    return dontDisplayDayOfWeek ? "PPP" : "PPPP";
                  case "month":
                    return "MMMM Y";
                  default:
                    assertNever(variant);
                }
              })()}
              value={value ? parseISO(value) : null}
              onChange={(date) => {
                onChange?.(
                  (() => {
                    if (!date) {
                      return null;
                    }

                    switch (variant) {
                      case "date":
                        return formatDateOnly(date);
                      case "month":
                        return formatMonthOnly(date);
                      default:
                        throw new Error(`Unrecognized variant ${variant}`);
                    }
                  })()
                );
              }}
              onBlur={onBlur}
              disabled={disabled || submitting || readOnly}
              // ensure helper text stays rendered so height doesn't jump around (unless hideHelperText is true):
              helperText={error?.message || (hideHelperText ? "" : " ")}
              error={Boolean(error)}
              views={[variant === "month" ? "month" : "date"]}
            />
          )}
        </ClassNames>
      </DateFieldProvider>
    );
  }
);
